import React, { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Typography, Spin, Table, Tag, Progress, Select, Space, Tooltip as AntTooltip } from 'antd';
import { 
  PieChart, 
  Pie, 
  Cell, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Legend,
  LabelList
} from 'recharts';
import api from '../api';
import { useFilters } from '../contexts/FilterContext';
import { useTheme } from '../contexts/ThemeContext';
import API_CONFIG from '../config';
import FullscreenChart from './FullscreenChart';
import '../responsive.css';

const { Title, Text } = Typography;
const { Option } = Select;

interface PipelineAnalysisProps {
  data: any;
  loading: boolean;
}

// Modern gradient color palette
const MODERN_COLORS = [
  '#6366f1', // Indigo
  '#8b5cf6', // Purple
  '#ec4899', // Pink
  '#f59e0b', // Amber
  '#10b981', // Emerald
  '#06b6d4', // Cyan
  '#3b82f6', // Blue
  '#ef4444', // Red
  '#f97316', // Orange
  '#14b8a6', // Teal
];

// Custom tooltip styling
const CustomTooltip = ({ active, payload, isDark }: any) => {
  if (active && payload && payload.length) {
    return (
      <div style={{
        backgroundColor: isDark ? '#1f1f1f' : 'rgba(255, 255, 255, 0.98)',
        border: `1px solid ${isDark ? '#434343' : '#e5e7eb'}`,
        borderRadius: '8px',
        padding: '12px 16px',
        boxShadow: isDark ? '0 4px 6px -1px rgba(0, 0, 0, 0.5), 0 2px 4px -1px rgba(0, 0, 0, 0.3)' : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        fontSize: '14px',
        fontWeight: 500
      }}>
        <div style={{ color: isDark ? 'rgba(255, 255, 255, 0.65)' : '#6b7280', marginBottom: '4px' }}>{payload[0].name}</div>
        <div style={{ color: isDark ? 'rgba(255, 255, 255, 0.85)' : '#111827', fontSize: '16px', fontWeight: 600 }}>
          {payload[0].value} {payload[0].value === 1 ? 'drug' : 'drugs'}
        </div>
      </div>
    );
  }
  return null;
};

const PipelineAnalysis: React.FC<PipelineAnalysisProps> = ({ data, loading }) => {
  const { isDark } = useTheme();
  const [phaseData, setPhaseData] = useState<any[]>([]);
  const [classificationData, setClassificationData] = useState<any[]>([]);
  const [indicationData, setIndicationData] = useState<any[]>([]);
  const [topDevelopersData, setTopDevelopersData] = useState<any[]>([]);
  const [pipelineDrugs, setPipelineDrugs] = useState<any[]>([]);
  const [chartLoading, setChartLoading] = useState(false);
  const [insights, setInsights] = useState({
    activeDrugs: 0,
    lateStagePercentage: 0,
    smallMoleculePercentage: 0,
    focusedPercentage: 0,
    focusedLabel: 'Melanoma Focused'
  });
  const [filterOptions, setFilterOptions] = useState<{ 
    phases: string[], 
    classifications: string[], 
    indications?: string[], 
    mechanisms?: string[], 
    drug_types?: string[], 
    routes?: string[] 
  }>({ phases: [], classifications: [] });
  const [fullscreenChart, setFullscreenChart] = useState<string | null>(null);
  const { filters } = useFilters();

  const fetchFilterOptions = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        status: filters.pipelineStatus
      });
      
      // Add therapeutic area if specified
      if (filters.therapeuticArea && filters.therapeuticArea !== 'All') {
        params.append('therapeutic_area', filters.therapeuticArea);
      }
      
      const response = await api.get(`/api/pipeline/filter-options?${params.toString()}`);
      setFilterOptions(response.data);
      console.log('FILTER OPTIONS UPDATED:', response.data);
    } catch (error) {
      console.error('ERROR FETCHING FILTER OPTIONS:', error);
      setFilterOptions({ phases: [], classifications: [] });
    }
  }, [filters.pipelineStatus, filters.therapeuticArea]);

  const fetchPipelineData = useCallback(async () => {
    try {
      setChartLoading(true);
      
      // Build filters and fetch pipeline drugs (single source of truth)
      const params = new URLSearchParams({ status: filters.pipelineStatus, limit: '500' });
      
      // Add therapeutic area parameter
      if (filters.therapeuticArea && filters.therapeuticArea !== 'All') {
        params.append('therapeutic_area', filters.therapeuticArea);
      }
      
      // Handle multiple companies selected
      if (filters.companySearchMulti && filters.companySearchMulti.length > 0) {
        params.append('developers', filters.companySearchMulti.join(','));
      }
      if (filters.phaseFilter && Array.isArray(filters.phaseFilter) && filters.phaseFilter.length > 0) {
        params.append('phase', filters.phaseFilter.join(','));
      }
      if (filters.classificationFilter && Array.isArray(filters.classificationFilter) && filters.classificationFilter.length > 0) {
        params.append('classification', filters.classificationFilter.join(','));
      }
      if (filters.mechanisms && Array.isArray(filters.mechanisms) && filters.mechanisms.length > 0) {
        params.append('mechanisms', filters.mechanisms.join(','));
      }
      if (filters.drugNameSearch) params.append('drug_name', filters.drugNameSearch);
      
      // Add Psoriasis-specific filters
      if (['Psoriasis', 'Psoriasis - Focus'].includes(filters.therapeuticArea)) {
        if (filters.mechanisms && Array.isArray(filters.mechanisms) && filters.mechanisms.length > 0) {
          params.append('mechanisms', filters.mechanisms.join(','));
        }
      }

      // Add Dermatology-specific filters
      if (['Dermatology', 'Dermatology - In Sight'].includes(filters.therapeuticArea)) {
        if (filters.drugTypes && Array.isArray(filters.drugTypes) && filters.drugTypes.length > 0) {
          params.append('drug_types', filters.drugTypes.join(','));
        }
      }

      // Add CGT/RNA-specific filters (Modality is stored in drug_type column)
      if (['Cell, Gene & RNA Therapies', 'Cell, Gene & RNA Therapies - In Sight'].includes(filters.therapeuticArea)) {
        if (filters.drugTypes && Array.isArray(filters.drugTypes) && filters.drugTypes.length > 0) {
          params.append('drug_types', filters.drugTypes.join(','));
        }
      }

      // Add common pipeline filters that apply to multiple therapeutic areas
      if (filters.indications && Array.isArray(filters.indications) && filters.indications.length > 0) {
        params.append('indications', filters.indications.join(','));
      }
      if (filters.routes && Array.isArray(filters.routes) && filters.routes.length > 0) {
        params.append('routes', filters.routes.join(','));
      }
      
      const drugsResponse = await api.get(`/api/pipeline/drugs?${params.toString()}`);
      if (filters.therapeuticArea === 'Psoriasis') {
        await api.get(`/api/pipeline/top-developers?therapeutic_area=Psoriasis&status=${filters.pipelineStatus}`);
        console.log('Psoriasis Pipeline Drugs Data:', drugsResponse.data.drugs);
        if (drugsResponse.data.drugs && drugsResponse.data.drugs.length > 0) {
          console.log('First drug structure:', drugsResponse.data.drugs[0]);
          console.log('Available fields:', Object.keys(drugsResponse.data.drugs[0]));
          console.log('First drug indications:', drugsResponse.data.drugs[0].indications);
          console.log('First drug route:', drugsResponse.data.drugs[0].route);
          console.log('First drug mechanism:', drugsResponse.data.drugs[0].mechanism);
          console.log('First drug therapeutic_area:', drugsResponse.data.drugs[0].therapeutic_area);
          console.log('First drug therapeutic_area_id:', drugsResponse.data.drugs[0].therapeutic_area_id);
          console.log('All drug fields with values:', Object.entries(drugsResponse.data.drugs[0]).filter(([key, value]) => value !== null && value !== undefined && value !== ''));
          
          // Debug indications specifically
          drugsResponse.data.drugs.forEach((drug: any, index: number) => {
            if (index < 3) { // Only log first 3 drugs
              console.log(`Drug ${index + 1} (${drug.name}):`, {
                indications: drug.indications,
                indicationsType: typeof drug.indications,
                indicationsLength: drug.indications?.length,
                isArray: Array.isArray(drug.indications)
              });
            }
          });
        }
      } else if (filters.therapeuticArea === 'Dermatology Pipeline') {
        console.log('Dermatology Pipeline Drugs Data:', drugsResponse.data.drugs);
        if (drugsResponse.data.drugs && drugsResponse.data.drugs.length > 0) {
          console.log('First Dermatology Pipeline drug:', drugsResponse.data.drugs[0]);
          console.log('Available fields:', Object.keys(drugsResponse.data.drugs[0]));
        }
      }
      setPipelineDrugs(drugsResponse.data.drugs || []);

      // Derive charts from filtered drugs
      const drugs: any[] = drugsResponse.data.drugs || [];

      // Phase distribution and insights - ensure no duplication by using unique drug names
      const phaseCounter: Record<string, number> = {};
      const uniqueDrugs = new Map();
      let lateStageCount = 0;
      let smallMoleculeCount = 0;
      let focusedCount = 0;
      let focusedLabel = 'Melanoma Focused';

      const isPsoriasis = filters.therapeuticArea.includes('Psoriasis');
      const isDermatology = filters.therapeuticArea.includes('Dermatology');
      const isGlaucoma = filters.therapeuticArea.includes('Glaucoma');
      const isRetinal = filters.therapeuticArea.includes('Retinal');
      const isOphthalmology = filters.therapeuticArea.includes('Ophthalmology') && !isGlaucoma && !isRetinal;
      const isCellGeneRna = filters.therapeuticArea.includes('Cell, Gene & RNA');

      if (isPsoriasis) {
        focusedLabel = 'Plaque Psoriasis Focused';
      } else if (isDermatology) {
        focusedLabel = 'Biologic Focused';
      } else if (isGlaucoma) {
        focusedLabel = 'Topical Focused';
      } else if (isRetinal) {
        focusedLabel = 'AMD Focused';
      } else if (isOphthalmology) {
        focusedLabel = 'Retinal Focused';
      } else if (isCellGeneRna) {
        focusedLabel = 'Oncology Focused';
      }
      
      drugs.forEach((d) => {
        const drugName = d.name;
        
        // Only count if we haven't seen this drug name before
        if (!uniqueDrugs.has(drugName)) {
          uniqueDrugs.set(drugName, true);
          
          const rawPhase = d.phase || 'Unknown';
          const cleanedPhase = cleanPhaseName(rawPhase);
          phaseCounter[cleanedPhase] = (phaseCounter[cleanedPhase] || 0) + 1;

          // Late stage (Phase II/III)
          if (['Phase II', 'Phase III', 'Phase II/III'].includes(cleanedPhase)) {
            lateStageCount++;
          }

          // Small Molecule - check classification, mechanism, drug_type, and route
          const classification = (d.classification || d.mechanism || '').toLowerCase();
          const drugType = (d.drug_type || '').toLowerCase();
          const route = (d.route || '').toLowerCase();

          const isBiologic = classification.includes('biologic') || 
                            classification.includes('monoclonal antibody') ||
                            classification.includes(' mabs') ||
                            classification.includes(' mab ') ||
                            classification.endsWith(' mab') ||
                            drugType.includes('biologic') ||
                            drugType.includes('monoclonal antibody') ||
                            drugType.includes(' antibody');

          if (drugType.includes('small molecule') || 
              classification.includes('small molecule') ||
              classification.includes('smd')) {
            smallMoleculeCount++;
          } else if (!isBiologic) {
            // If not explicitly a biologic, check for small molecule characteristics
            if (classification.includes('inhibitor') || 
                classification.includes('antagonist') || 
                classification.includes('modulator') ||
                classification.includes('blocker') ||
                route.includes('oral') ||
                route.includes('tablet') ||
                route.includes('capsule')) {
              smallMoleculeCount++;
            }
          }

          // Focused calculation
          if (isPsoriasis) {
            const indications = d.indications || [];
            const indList = Array.isArray(indications) ? indications : [indications];
            if (indList.some((ind: string) => ind && ind.toLowerCase().includes('plaque psoriasis'))) {
              focusedCount++;
            }
          } else if (isDermatology) {
            if (isBiologic) {
              focusedCount++;
            }
          } else if (isGlaucoma) {
            // For Glaucoma, check mechanism (stored in notes field) for common Glaucoma mechanisms
            // or check if it's a small molecule (most Glaucoma drugs are small molecules)
            const mechanism = (d.notes || d.mechanism || classification || '').toLowerCase();
            
            // Check for common Glaucoma mechanisms
            const hasGlaucomaMechanism = mechanism.includes('prostaglandin') ||
                                        mechanism.includes('rock') ||
                                        mechanism.includes('beta blocker') ||
                                        mechanism.includes('alpha agonist') ||
                                        mechanism.includes('cai') ||
                                        mechanism.includes('carbonic anhydrase') ||
                                        mechanism.includes('inhibitor') ||
                                        mechanism.includes('antagonist') ||
                                        mechanism.includes('blocker');
            
            // Or check if it's a small molecule (most Glaucoma drugs are small molecules)
            const isGlaucomaSmallMolecule = drugType.includes('small molecule') || 
                                           classification.includes('small molecule') ||
                                           (!isBiologic && (classification.includes('inhibitor') || 
                                                            classification.includes('antagonist') ||
                                                            classification.includes('blocker')));
            
            if (hasGlaucomaMechanism || isGlaucomaSmallMolecule) {
              focusedCount++;
            }
          } else if (isRetinal) {
            // For Retinal, check for AMD
            const indications = d.indication_types || [];
            const indList = Array.isArray(indications) ? indications : [indications];
            if (indList.some((ind: string) => {
              const indLower = String(ind).toLowerCase();
              return indLower.includes('amd') || 
                     indLower.includes('age-related macular degeneration');
            })) {
              focusedCount++;
            }
          } else if (isOphthalmology) {
            // For Ophthalmology, check for retinal-related indications
            const indications = d.indication_types || [];
            const indList = Array.isArray(indications) ? indications : [indications];
            if (indList.some((ind: string) => {
              const indLower = String(ind).toLowerCase();
              return indLower.includes('retinal') || 
                     indLower.includes('amd') ||
                     indLower.includes('diabetic retinopathy') ||
                     indLower.includes('rvo');
            })) {
              focusedCount++;
            }
          } else if (isCellGeneRna) {
            // For CGT/RNA, "focused" = oncology-area drugs (the largest disease cluster)
            // The disease therapeutic area arrives in the `notes` JSON from ETL or directly in classification.
            const notesRaw = (d.notes || '').toString();
            const classificationRaw = (d.classification || '').toLowerCase();
            let isOncology = false;
            try {
              const parsed = JSON.parse(notesRaw);
              if (parsed && typeof parsed === 'object') {
                const ta = String(parsed['Therapeutic Area'] || '').toLowerCase();
                const dc = String(parsed['Disease Category'] || '').toLowerCase();
                if (ta.includes('oncology') || dc.includes('tumor') || dc.includes('cancer') || dc.includes('lymphoma') || dc.includes('leukemia')) {
                  isOncology = true;
                }
              }
            } catch {
              // notes may not be JSON; fall back to substring match
            }
            if (!isOncology && classificationRaw.includes('oncology')) {
              isOncology = true;
            }
            if (isOncology) {
              focusedCount++;
            }
          } else {
            // Default to Melanoma for Skin Cancer
            const indications = d.indication_types || [];
            const indList = Array.isArray(indications) ? indications : [indications];
            if (indList.some((ind: string) => ind && ind.toLowerCase().includes('melanoma'))) {
              focusedCount++;
            }
          }
        } else {
          console.warn(`DUPLICATE DRUG FOUND: ${drugName}`);
        }
      });
      
      const totalUnique = uniqueDrugs.size;
      setInsights({
        activeDrugs: totalUnique,
        lateStagePercentage: totalUnique > 0 ? Math.round((lateStageCount / totalUnique) * 100) : 0,
        smallMoleculePercentage: totalUnique > 0 ? Math.round((smallMoleculeCount / totalUnique) * 100) : 0,
        focusedPercentage: totalUnique > 0 ? Math.round((focusedCount / totalUnique) * 100) : 0,
        focusedLabel
      });

      console.log('UNIQUE DRUGS COUNTED:', uniqueDrugs.size);
      console.log('PHASE COUNTER:', phaseCounter);
      console.log('INSIGHTS CALCULATED:', {
        totalUnique,
        lateStageCount,
        smallMoleculeCount,
        focusedCount,
        focusedLabel
      });
      
      const phaseSeries = Object.entries(phaseCounter).map(([k, v]) => ({ name: k, value: v }));
      setPhaseData(phaseSeries);

      // Classification or Indication distribution based on therapeutic area
      if (filters.therapeuticArea === 'Psoriasis') {
        const indicationCounter: Record<string, number> = {};
        const uniqueDrugsForIndication = new Map();
        
        drugs.forEach((d) => {
          const drugName = d.name;
          if (!uniqueDrugsForIndication.has(drugName)) {
            uniqueDrugsForIndication.set(drugName, true);
            // The indication data is actually in the 'route' field for Psoriasis
            if (d.route && d.route !== 'Unknown') {
              // Split by comma in case there are multiple indications
              const indications = d.route.split(',').map((ind: string) => ind.trim());
              indications.forEach((indication: string) => {
                if (indication && indication !== 'Unknown') {
                  // Normalize the indication name to handle case differences
                  const normalizedIndication = indication
                    .toLowerCase()
                    .replace(/\b\w/g, (l: string) => l.toUpperCase()) // Title case
                    .replace(/\s*\([^)]*\)/g, '') // Remove parentheses and content
                    .trim();
                  
                  // Handle specific known variations
                  let finalIndication = normalizedIndication;
                  if (normalizedIndication.includes('Generalized Pustular Psoriasis') || normalizedIndication.includes('Generalised Pustular Psoriasis')) {
                    finalIndication = 'Generalized Pustular Psoriasis (GPP)';
                  } else if (normalizedIndication.includes('Palmoplantar Pustulosis')) {
                    finalIndication = 'Palmoplantar Pustulosis (PPP)';
                  } else if (normalizedIndication.includes('Psoriatic Arthritis')) {
                    finalIndication = 'Psoriatic Arthritis';
                  } else if (normalizedIndication.includes('Plaque Psoriasis')) {
                    finalIndication = 'Plaque Psoriasis';
                  }
                  
                  indicationCounter[finalIndication] = (indicationCounter[finalIndication] || 0) + 1;
                }
              });
            }
          }
        });
        
        console.log('INDICATION COUNTER (Psoriasis):', indicationCounter);
        const indicationSeries = Object.entries(indicationCounter).map(([k, v]) => ({ name: k, value: v }));
        setIndicationData(indicationSeries);
        setClassificationData([]); // Clear other data
      } else {
        // Default to Skin Cancer classification logic
        const classCounter: Record<string, number> = {};
        const uniqueDrugsForClass = new Map();
        
        drugs.forEach((d) => {
          const drugName = d.name;
          const classification = d.classification || 'Unknown';
          
          if (!uniqueDrugsForClass.has(drugName)) {
            uniqueDrugsForClass.set(drugName, true);
            classCounter[classification] = (classCounter[classification] || 0) + 1;
          }
        });
        
        console.log('CLASSIFICATION COUNTER (Skin Cancer):', classCounter);
        const classSeries = Object.entries(classCounter).map(([k, v]) => ({ name: k, value: v }));
        setClassificationData(classSeries);
        setIndicationData([]); // Clear other data
      }

      // Top developers - ensure no duplication by using unique drug names
      const devCounter: Record<string, number> = {};
      const uniqueDrugsForDev = new Map(); // Reset for developer counting
      
      drugs.forEach((d) => {
        const drugName = d.name;
        
        // Only count if we haven't seen this drug name before
        if (!uniqueDrugsForDev.has(drugName)) {
          uniqueDrugsForDev.set(drugName, true);
          (d.developers || []).forEach((dev: string) => {
            devCounter[dev] = (devCounter[dev] || 0) + 1;
          });
        }
      });
      
      console.log('DEVELOPER COUNTER:', devCounter);
      const devSeries = Object.entries(devCounter)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 10);
      setTopDevelopersData(devSeries);

    } catch (error) {
      console.error('ERROR FETCHING PIPELINE DATA:', error);
      // NO MOCK DATA - ONLY REAL DATABASE DATA
      console.error('FAILED TO FETCH REAL PIPELINE DATA - SHOWING EMPTY STATE');
      setPhaseData([]);
      setClassificationData([]);
      setIndicationData([]);
      setTopDevelopersData([]);
      setPipelineDrugs([]);
    } finally {
      setChartLoading(false);
    }
  }, [
    filters.pipelineStatus,
    filters.companySearchMulti,
    filters.phaseFilter,
    filters.classificationFilter,
    filters.drugNameSearch,
    filters.therapeuticArea,
    filters.mechanisms,
    filters.indications,
    filters.routes,
    filters.drugTypes,
  ]);

  useEffect(() => {
    fetchPipelineData();
  }, [fetchPipelineData]);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  // Unused function - kept for potential future use
  // const transformPieData = (chartResponse: any) => {
  //   if (!chartResponse.series || chartResponse.series.length === 0) {
  //     return [];
  //   }
  //   return chartResponse.series[0].data.map((point: any) => ({
  //     name: point.x,
  //     value: point.y
  //   }));
  // };

  const transformBarData = (chartResponse: any) => {
    if (!chartResponse.series || chartResponse.series.length === 0) {
      return [];
    }

    const rows = chartResponse.series[0].data.map((point: any) => ({
      name: point.x,
      value: point.y
    }));

    // Sort descending, keep top 10 for clarity
    return rows.sort((a: any, b: any) => b.value - a.value).slice(0, 10);
  };

  // REMOVED ALL MOCK DATA FUNCTIONS - ONLY REAL DATABASE DATA ALLOWED
  // Market research companies cannot use fake data under any circumstances

  // Clean phase names to remove status information and standardize format
  const cleanPhaseName = (phase: string): string => {
    if (!phase) return 'Unknown';
    
    // Remove status information from phase names
    const cleaned = phase
      .replace(/\s*\(discontinued\)/i, '')
      .replace(/\s*\(suspended\)/i, '')
      .replace(/\s*\(unknown\)/i, '')
      .replace(/\s*\(inactive\)/i, '')
      .trim();
    
    // Standardize common phase variations
    const phaseMap: { [key: string]: string } = {
      'phase i/ii': 'Phase I/II',
      'phase ii/iii': 'Phase II/III',
      'phase i': 'Phase I',
      'phase ii': 'Phase II',
      'phase iii': 'Phase III',
      'pre-clinical': 'Pre-Clinical',
      'preclinical': 'Pre-Clinical',
      'pre-registration': 'Pre-Registration',
      'preregistration': 'Pre-Registration'
    };
    
    const lowerPhase = cleaned.toLowerCase();
    return phaseMap[lowerPhase] || cleaned;
  };

  const psoriasisPipelineColumns = [
    {
      title: 'Candidate',
      dataIndex: 'name',
      key: 'name',
      width: 160,
      render: (text: string, record: any) => (
        <AntTooltip 
          title={
            <div style={{ color: isDark ? 'rgba(255, 255, 255, 0.85)' : 'black', fontSize: '14px' }}>
              {record.description || 'No description available'}
            </div>
          }
          placement="topLeft"
          overlayStyle={{ 
            maxWidth: '400px',
            backgroundColor: isDark ? '#1f1f1f' : 'white',
            border: `1px solid ${isDark ? '#434343' : '#d9d9d9'}`,
            borderRadius: '6px',
            boxShadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.5)' : '0 2px 8px rgba(0, 0, 0, 0.15)'
          }}
          color={isDark ? '#1f1f1f' : 'white'}
        >
          <Text strong style={{ cursor: 'help' }}>{text}</Text>
        </AntTooltip>
      )
    },
    {
      title: 'Developer(s)',
      dataIndex: 'developers',
      key: 'developers',
      width: 140,
      render: (developers: string[]) => 
        (developers || []).slice(0, 2).map(dev => (
          <Tag key={dev} color="blue" style={{ marginBottom: '4px' }}>
            {dev}
          </Tag>
        ))
    },
    {
      title: 'Indication(s)',
      dataIndex: 'indications',
      key: 'indications',
      width: 160,
      render: (indications: string[]) => {
        if (!indications || indications.length === 0) return <Tag color="default">Unknown</Tag>;
        return indications.slice(0, 2).map(indication => (
          <Tag key={indication} color="green" style={{ marginBottom: '4px' }}>
            {indication}
          </Tag>
        ));
      }
    },
    {
      title: 'Phase',
      dataIndex: 'phase',
      key: 'phase',
      width: 100,
      render: (phase: string) => (
        <div>
          <Tag color={getPhaseColor(cleanPhaseName(phase))}>{cleanPhaseName(phase)}</Tag>
          <Progress 
            percent={getPhaseProgress(cleanPhaseName(phase))} 
            showInfo={false} 
            size="small"
            strokeColor={getPhaseColor(cleanPhaseName(phase))}
          />
        </div>
      )
    },
    {
      title: 'Mechanism(s)',
      dataIndex: 'mechanism',
      key: 'mechanism',
      width: 180,
      render: (mechanism: string) => (
        <Tag color="cyan">{mechanism}</Tag>
      )
    },
    {
      title: 'Route of Administration',
      dataIndex: 'route',
      key: 'route',
      width: 160,
      render: (route: string) => <Tag color="purple">{route}</Tag>
    },
  ];

  const getPhaseColor = (phase: string) => {
    const phaseColors: { [key: string]: string } = {
      'Phase III': '#52c41a',
      'Phase II': '#1890ff',
      'Phase I': '#faad14',
      'Phase I/II': '#722ed1', // Purple
      'Phase II/III': '#10b981', // Emerald - clearly different from purple
      'Pre-Clinical': '#f5222d',
      'Pre-Registration': '#13c2c2',
      'Approved': '#eb2f96',
      'Marketed': '#8c8c8c'
    };
    return phaseColors[phase] || '#d9d9d9';
  };

  // Get color for phase data in pie chart
  const getPhaseDataColor = (phaseName: string) => {
    return getPhaseColor(phaseName);
  };

  const getPhaseProgress = (phase: string) => {
    const phaseProgress: { [key: string]: number } = {
      'Pre-Clinical': 10,
      'Phase I': 25,
      'Phase I/II': 40,
      'Phase II': 60,
      'Phase II/III': 80,
      'Phase III': 90,
      'Pre-Registration': 95,
      'Approved': 100,
      'Marketed': 100
    };
    return phaseProgress[phase] || 0;
  };

  const pipelineColumns = [
    {
      title: 'Drug Name',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      render: (text: string, record: any) => (
        <AntTooltip 
          title={
            <div style={{ color: isDark ? 'rgba(255, 255, 255, 0.85)' : 'black', fontSize: '14px' }}>
              {record.description || 'No description available'}
            </div>
          }
          placement="topLeft"
          overlayStyle={{ 
            maxWidth: '400px',
            backgroundColor: isDark ? '#1f1f1f' : 'white',
            border: `1px solid ${isDark ? '#434343' : '#d9d9d9'}`,
            borderRadius: '6px',
            boxShadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.5)' : '0 2px 8px rgba(0, 0, 0, 0.15)'
          }}
          color={isDark ? '#1f1f1f' : 'white'}
        >
          <Text strong style={{ cursor: 'help' }}>{text}</Text>
        </AntTooltip>
      )
    },
    {
      title: 'Developer(s)',
      dataIndex: 'developers',
      key: 'developers',
      width: 150,
      render: (developers: string[]) => 
        (developers || []).map(dev => (
          <Tag key={dev} color="blue" style={{ marginBottom: '4px' }}>
            {dev}
          </Tag>
        ))
    },
    {
      title: 'Phase',
      dataIndex: 'phase',
      key: 'phase',
      width: 120,
      render: (phase: string) => (
        <div>
          <Tag color={getPhaseColor(cleanPhaseName(phase))}>{cleanPhaseName(phase)}</Tag>
          <Progress 
            percent={getPhaseProgress(cleanPhaseName(phase))} 
            showInfo={false} 
            size="small"
            strokeColor={getPhaseColor(cleanPhaseName(phase))}
          />
        </div>
      )
    },
    {
      title: 'Classification',
      dataIndex: 'classification',
      key: 'classification',
      width: 150,
      render: (classification: string) => (
        <Tag color="purple">{classification}</Tag>
      )
    },
    {
      title: 'Indication',
      dataIndex: 'indication_types',
      key: 'indication_types',
      width: 150,
      render: (indications: string[]) => 
        (indications || []).map(indication => (
          <Tag key={indication} color="green" style={{ marginBottom: '4px' }}>
            {indication}
          </Tag>
        ))
    },
  ];

  // Dermatology-specific columns (includes Drug Type and Route)
  const dermatologyPipelineColumns = [
    {
      title: 'Drug Name',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      render: (text: string, record: any) => (
        <AntTooltip 
          title={
            <div style={{ color: isDark ? 'rgba(255, 255, 255, 0.85)' : 'black', fontSize: '14px' }}>
              {record.description || 'No description available'}
            </div>
          }
          placement="topLeft"
          overlayStyle={{ 
            maxWidth: '400px',
            backgroundColor: isDark ? '#1f1f1f' : 'white',
            border: `1px solid ${isDark ? '#434343' : '#d9d9d9'}`,
            borderRadius: '6px',
            boxShadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.5)' : '0 2px 8px rgba(0, 0, 0, 0.15)'
          }}
          color={isDark ? '#1f1f1f' : 'white'}
        >
          <Text strong style={{ cursor: 'help' }}>{text}</Text>
        </AntTooltip>
      )
    },
    {
      title: 'Developer(s)',
      dataIndex: 'developers',
      key: 'developers',
      width: 150,
      render: (developers: string[]) => 
        (developers || []).map(dev => (
          <Tag key={dev} color="blue" style={{ marginBottom: '4px' }}>
            {dev}
          </Tag>
        ))
    },
    {
      title: 'Phase',
      dataIndex: 'phase',
      key: 'phase',
      width: 120,
      render: (phase: string) => (
        <div>
          <Tag color={getPhaseColor(cleanPhaseName(phase))}>{cleanPhaseName(phase)}</Tag>
          <Progress 
            percent={getPhaseProgress(cleanPhaseName(phase))} 
            showInfo={false} 
            size="small"
            strokeColor={getPhaseColor(cleanPhaseName(phase))}
          />
        </div>
      )
    },
    {
      title: 'Classification',
      dataIndex: 'classification',
      key: 'classification',
      width: 150,
      render: (classification: string) => (
        <Tag color="purple">{classification}</Tag>
      )
    },
    {
      title: 'Indication',
      dataIndex: 'indication_types',
      key: 'indication_types',
      width: 150,
      render: (indications: string[]) => 
        (indications || []).map(indication => (
          <Tag key={indication} color="green" style={{ marginBottom: '4px' }}>
            {indication}
          </Tag>
        ))
    },
    {
      title: 'Drug Type',
      dataIndex: 'drug_type',
      key: 'drug_type',
      width: 150,
      render: (drugType: string) => (
        <Tag color="orange">{drugType}</Tag>
      )
    },
    {
      title: 'Route',
      dataIndex: 'route',
      key: 'route',
      width: 120,
      render: (route: string) => (
        <Tag color="cyan">{route}</Tag>
      )
    },
  ];

  // Cell, Gene & RNA pipeline columns - rich schema (Therapy Class, Therapeutic Approach,
  // Development Stage, Modality, Mechanism of Action, Status). Status column makes
  // Approved rows visible alongside Active and Discontinued/Suspended.
  const parseCgtNotes = (notes: any): Record<string, string> => {
    if (!notes) return {};
    try {
      const parsed = typeof notes === 'string' ? JSON.parse(notes) : notes;
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  };

  const cgtRnaPipelineColumns = [
    {
      title: 'Drug Name',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      render: (text: string, record: any) => (
        <AntTooltip
          title={
            <div style={{ color: isDark ? 'rgba(255, 255, 255, 0.85)' : 'black', fontSize: '14px' }}>
              {record.description || 'No description available'}
            </div>
          }
          placement="topLeft"
          overlayStyle={{
            maxWidth: '400px',
            backgroundColor: isDark ? '#1f1f1f' : 'white',
            border: `1px solid ${isDark ? '#434343' : '#d9d9d9'}`,
            borderRadius: '6px',
            boxShadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.5)' : '0 2px 8px rgba(0, 0, 0, 0.15)',
          }}
          color={isDark ? '#1f1f1f' : 'white'}
        >
          <Text strong style={{ cursor: 'help' }}>{text}</Text>
        </AntTooltip>
      ),
    },
    {
      title: 'Developer(s)',
      dataIndex: 'developers',
      key: 'developers',
      width: 160,
      render: (developers: string[]) =>
        (developers || []).map((dev) => (
          <Tag key={dev} color="blue" style={{ marginBottom: '4px' }}>{dev}</Tag>
        )),
    },
    {
      title: 'Therapy Class',
      dataIndex: 'classification',
      key: 'classification',
      width: 160,
      render: (classification: string) => classification ? <Tag color="purple">{classification}</Tag> : null,
    },
    {
      title: 'Therapeutic Approach',
      key: 'therapeutic_approach',
      width: 160,
      render: (_: any, record: any) => {
        const n = parseCgtNotes(record.notes);
        const approach = n['Therapeutic Approach'] || record.therapeutic_approach;
        return approach ? <Tag color="geekblue">{approach}</Tag> : null;
      },
    },
    {
      title: 'Modality',
      dataIndex: 'drug_type',
      key: 'drug_type',
      width: 140,
      render: (drugType: string) => drugType ? <Tag color="orange">{drugType}</Tag> : null,
    },
    {
      title: 'Development Stage',
      dataIndex: 'phase',
      key: 'phase',
      width: 140,
      render: (phase: string) => (
        <div>
          <Tag color={getPhaseColor(cleanPhaseName(phase))}>{cleanPhaseName(phase)}</Tag>
        </div>
      ),
    },
    {
      title: 'Indication',
      dataIndex: 'indication_types',
      key: 'indication_types',
      width: 180,
      render: (indications: string[]) =>
        (indications || []).map((indication) => (
          <Tag key={indication} color="green" style={{ marginBottom: '4px' }}>{indication}</Tag>
        )),
    },
    {
      title: 'Mechanism of Action',
      key: 'mechanism_of_action',
      width: 200,
      render: (_: any, record: any) => {
        const n = parseCgtNotes(record.notes);
        const moa = n['Mechanism of Action'];
        return moa ? <Text style={{ fontSize: '12px' }}>{moa}</Text> : null;
      },
    },
    {
      title: 'Delivery',
      key: 'delivery_technology',
      width: 140,
      render: (_: any, record: any) => {
        const n = parseCgtNotes(record.notes);
        const dt = n['Delivery Technology'] || n['Delivery Platform'];
        return dt ? <Tag color="cyan">{dt}</Tag> : null;
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => {
        const colorMap: Record<string, string> = {
          Active: 'blue',
          Approved: 'green',
          Discontinued: 'red',
          Suspended: 'orange',
          Marketed: 'purple',
        };
        return status ? <Tag color={colorMap[status] || 'default'}>{status}</Tag> : null;
      },
    },
  ];

  const currentColumns = filters.therapeuticArea.includes('Psoriasis')
    ? psoriasisPipelineColumns 
    : filters.therapeuticArea.includes('Dermatology')
    ? dermatologyPipelineColumns 
    : filters.therapeuticArea.includes('Cell, Gene & RNA')
    ? cgtRnaPipelineColumns
    : (filters.therapeuticArea.includes('Ophthalmology') || 
       filters.therapeuticArea.includes('Retinal') || 
       filters.therapeuticArea.includes('Glaucoma'))
    ? pipelineColumns  // Ophthalmology uses standard columns with indication_types
    : pipelineColumns;

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>
          <Text>Loading pipeline data...</Text>
        </div>
      </div>
    );
  }

  return (
    <div>

      <Row gutter={[16, 16]}>
        {/* Phase Distribution */}
        <Col xs={24} sm={24} md={12} lg={12} xl={12}>
          <Card 
            title={
              <div>
                <Title level={5} style={{ margin: 0 }}>Development Phase Distribution</Title>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {filters.pipelineStatus} pipeline drugs by phase
                </Text>
              </div>
            }
            loading={chartLoading}
          >
            <FullscreenChart
              chartId="phase-distribution"
              title="Development Phase Distribution"
              isFullscreen={fullscreenChart === 'phase-distribution'}
              onToggleFullscreen={() => setFullscreenChart(fullscreenChart === 'phase-distribution' ? null : 'phase-distribution')}
              fullscreenContent={
                <ResponsiveContainer width="100%" height="100%">
                  {phaseData.length > 0 ? (
                    <PieChart>
                      <Pie
                        data={phaseData}
                        cx="50%"
                        cy="50%"
                        innerRadius={80}
                        outerRadius={200}
                        paddingAngle={2}
                        dataKey="value"
                        animationBegin={0}
                        animationDuration={800}
                        animationEasing="ease-out"
                      >
                        {phaseData.map((entry: any, index: number) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={getPhaseDataColor(entry.name)}
                            stroke="#fff"
                            strokeWidth={2}
                            style={{
                              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                              transition: 'opacity 0.3s'
                            }}
                          />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip isDark={isDark} />} />
                      <Legend 
                        verticalAlign="middle" 
                        align="right" 
                        layout="vertical"
                        iconSize={14}
                        iconType="circle"
                        wrapperStyle={{ 
                          fontSize: '14px', 
                          paddingLeft: '20px',
                          fontWeight: 500,
                          color: '#374151'
                        }}
                      />
                    </PieChart>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                      <Text type="secondary">No phase data available</Text>
                    </div>
                  )}
                </ResponsiveContainer>
              }
            >
              <ResponsiveContainer width="100%" height={300}>
                {phaseData.length > 0 ? (
                  <PieChart>
                     <Pie
                       data={phaseData}
                       cx="45%"
                       cy="50%"
                       innerRadius={30}
                       outerRadius={80}
                       paddingAngle={2}
                       dataKey="value"
                       animationBegin={0}
                       animationDuration={800}
                       animationEasing="ease-out"
                     >
                       {phaseData.map((entry: any, index: number) => (
                         <Cell 
                           key={`cell-${index}`} 
                           fill={getPhaseDataColor(entry.name)}
                           stroke="#fff"
                           strokeWidth={2}
                           style={{
                             filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                             transition: 'opacity 0.3s'
                           }}
                         />
                       ))}
                     </Pie>
                    <Tooltip content={<CustomTooltip isDark={isDark} />} />
                    <Legend 
                      verticalAlign="middle" 
                      align="right" 
                      layout="vertical"
                      iconSize={10}
                      iconType="circle"
                      wrapperStyle={{ 
                        fontSize: '12px', 
                        paddingLeft: '10px',
                        fontWeight: 500,
                        color: '#374151'
                      }}
                    />
                  </PieChart>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <Text type="secondary">No phase data available</Text>
                  </div>
                )}
              </ResponsiveContainer>
            </FullscreenChart>
          </Card>
        </Col>

        {/* Classification / Indication Distribution */}
        <Col xs={24} sm={24} md={12} lg={12} xl={12}>
          {filters.therapeuticArea === 'Psoriasis' ? (
            <Card 
              title={
                <div>
                  <Title level={5} style={{ margin: 0 }}>Indication Distribution</Title>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Distribution by indication type
                  </Text>
                </div>
              }
              loading={chartLoading}
            >
              <FullscreenChart
                chartId="indication-distribution"
                title="Indication Distribution"
                isFullscreen={fullscreenChart === 'indication-distribution'}
                onToggleFullscreen={() => setFullscreenChart(fullscreenChart === 'indication-distribution' ? null : 'indication-distribution')}
                fullscreenContent={
                  <ResponsiveContainer width="100%" height="100%">
                    {indicationData.length > 0 ? (
                      <PieChart>
                        <Pie
                          data={indicationData}
                          cx="50%"
                          cy="50%"
                          innerRadius={80}
                          outerRadius={200}
                          paddingAngle={2}
                          dataKey="value"
                          animationBegin={0}
                          animationDuration={800}
                          animationEasing="ease-out"
                        >
                          {indicationData.map((entry: any, index: number) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={MODERN_COLORS[index % MODERN_COLORS.length]}
                              stroke="#fff"
                              strokeWidth={2}
                              style={{
                                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                                transition: 'opacity 0.3s'
                              }}
                            />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip isDark={isDark} />} />
                        <Legend 
                          verticalAlign="middle" 
                          align="right" 
                          layout="vertical"
                          iconSize={14}
                          iconType="circle"
                          wrapperStyle={{ 
                            fontSize: '14px', 
                            paddingLeft: '20px',
                            fontWeight: 500,
                            color: isDark ? 'rgba(255, 255, 255, 0.85)' : '#374151'
                          }}
                        />
                      </PieChart>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <Text type="secondary">No indication data available</Text>
                      </div>
                    )}
                  </ResponsiveContainer>
                }
              >
                <ResponsiveContainer width="100%" height={300}>
                  {indicationData.length > 0 ? (
                    <PieChart>
                      <Pie
                        data={indicationData}
                        cx="45%"
                        cy="50%"
                        innerRadius={30}
                        outerRadius={80}
                        paddingAngle={2}
                        dataKey="value"
                        animationBegin={0}
                        animationDuration={800}
                        animationEasing="ease-out"
                      >
                        {indicationData.map((entry: any, index: number) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={MODERN_COLORS[index % MODERN_COLORS.length]}
                            stroke="#fff"
                            strokeWidth={2}
                            style={{
                              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                              transition: 'opacity 0.3s'
                            }}
                          />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip isDark={isDark} />} />
                      <Legend 
                        verticalAlign="middle" 
                        align="right" 
                        layout="vertical"
                        iconSize={10}
                        iconType="circle"
                        wrapperStyle={{ 
                          fontSize: '12px', 
                          paddingLeft: '10px',
                          fontWeight: 500,
                          color: '#374151'
                        }}
                      />
                    </PieChart>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                      <Text type="secondary">No indication data available</Text>
                    </div>
                  )}
                </ResponsiveContainer>
              </FullscreenChart>
            </Card>
          ) : (
            <Card 
              title={
                <div>
                  <Title level={5} style={{ margin: 0 }}>Drug Classification</Title>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Distribution by drug type
                  </Text>
                </div>
              }
              loading={chartLoading}
            >
              <FullscreenChart
                chartId="classification-distribution"
                title="Drug Classification"
                isFullscreen={fullscreenChart === 'classification-distribution'}
                onToggleFullscreen={() => setFullscreenChart(fullscreenChart === 'classification-distribution' ? null : 'classification-distribution')}
                fullscreenContent={
                  <ResponsiveContainer width="100%" height="100%">
                    {classificationData.length > 0 ? (
                      <PieChart>
                        <Pie
                          data={classificationData}
                          cx="50%"
                          cy="50%"
                          innerRadius={80}
                          outerRadius={200}
                          paddingAngle={2}
                          dataKey="value"
                          animationBegin={0}
                          animationDuration={800}
                          animationEasing="ease-out"
                        >
                          {classificationData.map((entry: any, index: number) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={MODERN_COLORS[index % MODERN_COLORS.length]}
                              stroke="#fff"
                              strokeWidth={2}
                              style={{
                                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                                transition: 'opacity 0.3s'
                              }}
                            />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip isDark={isDark} />} />
                        <Legend 
                          verticalAlign="middle" 
                          align="right" 
                          layout="vertical"
                          iconSize={14}
                          iconType="circle"
                          wrapperStyle={{ 
                            fontSize: '14px', 
                            paddingLeft: '20px',
                            fontWeight: 500,
                            color: isDark ? 'rgba(255, 255, 255, 0.85)' : '#374151'
                          }}
                        />
                      </PieChart>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <Text type="secondary">No classification data available</Text>
                      </div>
                    )}
                  </ResponsiveContainer>
                }
              >
                <ResponsiveContainer width="100%" height={300}>
                  {classificationData.length > 0 ? (
                    <PieChart>
                      <Pie
                        data={classificationData}
                        cx="45%"
                        cy="50%"
                        innerRadius={30}
                        outerRadius={80}
                        paddingAngle={2}
                        dataKey="value"
                        animationBegin={0}
                        animationDuration={800}
                        animationEasing="ease-out"
                      >
                        {classificationData.map((entry: any, index: number) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={MODERN_COLORS[index % MODERN_COLORS.length]}
                            stroke="#fff"
                            strokeWidth={2}
                            style={{
                              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                              transition: 'opacity 0.3s'
                            }}
                          />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip isDark={isDark} />} />
                      <Legend 
                        verticalAlign="middle" 
                        align="right" 
                        layout="vertical"
                        iconSize={10}
                        iconType="circle"
                        wrapperStyle={{ 
                          fontSize: '12px', 
                          paddingLeft: '10px',
                          fontWeight: 500,
                          color: '#374151'
                        }}
                      />
                    </PieChart>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                      <Text type="secondary">No classification data available</Text>
                    </div>
                  )}
                </ResponsiveContainer>
              </FullscreenChart>
            </Card>
          )}
        </Col>

        {/* Pipeline Drugs Table */}
        <Col span={24}>
          <Card 
            title={
              <div>
                <Title level={5} style={{ margin: 0 }}>Pipeline Drugs Detail</Title>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Detailed view of {filters.pipelineStatus} pipeline drugs
                </Text>
              </div>
            }
            loading={chartLoading}
          >
            {pipelineDrugs.length > 0 ? (
              <Table
                columns={currentColumns}
                dataSource={pipelineDrugs}
                rowKey="name"
                pagination={{ 
                  pageSize: 10,
                  showSizeChanger: false,
                  showQuickJumper: true,
                  showTotal: (total, range) => 
                    `${range[0]}-${range[1]} of ${total} drugs`
                }}
                scroll={{ x: 900 }}
                size="small"
                tableLayout="fixed"
                style={{ 
                  width: '100%',
                  minWidth: '900px'
                }}
              />
            ) : (
              <div style={{ textAlign: 'center', padding: '32px' }}>
                <Text type="secondary">No pipeline drugs available</Text>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Pipeline Insights */}
      <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
        <Col span={24}>
          <Card title="Pipeline Insights">
            <Row gutter={[16, 16]}>
              <Col span={6}>
                <div style={{ textAlign: 'center', padding: '16px' }}>
                  <Title level={4} style={{ color: '#1890ff', margin: 0 }}>{insights.activeDrugs}</Title>
                  <Text type="secondary">Active Pipeline Drugs</Text>
                </div>
              </Col>
              <Col span={6}>
                <div style={{ textAlign: 'center', padding: '16px' }}>
                  <Title level={4} style={{ color: '#52c41a', margin: 0 }}>{insights.lateStagePercentage}%</Title>
                  <Text type="secondary">Phase II/III Drugs</Text>
                </div>
              </Col>
              <Col span={6}>
                <div style={{ textAlign: 'center', padding: '16px' }}>
                  <Title level={4} style={{ color: '#faad14', margin: 0 }}>{insights.smallMoleculePercentage}%</Title>
                  <Text type="secondary">Small Molecule Drugs</Text>
                </div>
              </Col>
              <Col span={6}>
                <div style={{ textAlign: 'center', padding: '16px' }}>
                  <Title level={4} style={{ color: '#722ed1', margin: 0 }}>{insights.focusedPercentage}%</Title>
                  <Text type="secondary">{insights.focusedLabel}</Text>
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default PipelineAnalysis;